import {
  AI_BUILDER_CARD_VISIBLE_OPTIONS,
  AiBuilderAbout,
  AiBuilderCard,
  AiBuilderMessageKey,
  AiBuilderSystemMessage,
  AiBuilderText,
  AiBuilderTurnInput,
  AskAiBuilderResponse,
  cardTargetOf,
  CardDropdownOption,
  CardTarget,
  flowStructureUtil,
  FlowVersion,
  GenerateFlowRequest,
  isKeyText,
  isNil,
  keyText,
  NavigateDirective,
  PendingQuestionDisplay,
  ReplyTo,
  SuggestedAction,
  TurnChanges,
} from '@activepieces/shared';
import { Static, Type } from '@sinclair/typebox';
import deepEqual from 'deep-equal';

import { AVAILABLE_PIECES_TRANSLATE } from '../../../available-pieces-translate';
import { canvasGraphKey } from '../flow-canvas/utils/graph-key';

// The chat view-model — not @activepieces/shared's AiBuilderMessage, the
// {role, content} wire shape. Lives here, NOT in chat-message.tsx: that module
// pulls react-markdown (pure ESM, unparseable by Jest), so anything a test
// imports — the store, the turn hook — must reach this type without touching it.
export const AiBuilderChatMessage = Type.Union([
  Type.Object({
    userType: Type.Union([Type.Literal('user'), Type.Literal('bot')]),
    /** A Tolgee key (with params) or literal text — never a convention read from whether params is present */
    body: AiBuilderText,
    /** Model-written free text shown above `body`, never translated */
    prefix: Type.Optional(Type.String()),
    /** What the turn changed, from the persisted flow: folded under the answer */
    changes: Type.Optional(TurnChanges),
    previousFlowVersion: Type.Optional(Type.Any()),
    newFlowVersion: Type.Optional(Type.Any()),
    suggestedActions: Type.Optional(Type.Array(SuggestedAction)),
    card: Type.Optional(AiBuilderCard),
    answeredChoice: Type.Optional(Type.String()),
    /**
     * What Retry resends. A message stored before the v1→v2 storage migration
     * never carries it: a turn answered by the previous release is not replayable.
     */
    sourceInput: Type.Optional(AiBuilderTurnInput),
    /**
     * The "about" this turn was sent with, kept on a FAILED turn's bot message
     * so Retry can replay it: `takePendingAbout` already cleared the store's
     * own pointer when the turn was first sent.
     */
    sourceAbout: Type.Optional(AiBuilderAbout),
    /**
     * The reply chip's own state (`GenerateFlowRequest['replyTo']`) when
     * this turn was sent, kept alongside `sourceInput`/`sourceAbout` on a
     * FAILED turn's bot message so Retry resends the SAME value — the
     * chip itself may have moved on by the time the user clicks Retry.
     */
    sourceReplyTo: Type.Optional(ReplyTo),
    flowReady: Type.Optional(Type.Boolean()),
    /** A line the assistant reports mid-turn — never the turn's answer */
    narration: Type.Optional(Type.Boolean()),
    navigateDirectives: Type.Optional(Type.Array(NavigateDirective)),
    /** What this bot answer is waiting on — the "Réponse à" chip reads it */
    pendingQuestion: Type.Optional(PendingQuestionDisplay),
    /** The user dismissed the chip for this answer with its cross */
    replyChipDismissed: Type.Optional(Type.Boolean()),
  }),
]);
export type AiBuilderChatMessage = Static<typeof AiBuilderChatMessage>;

/** Field-name lists arrive bolded and comma-joined: "**Subject**, **Body**" */
const BOLD_FIELD_NAME = /\*\*([^*]+)\*\*/g;

/**
 * Translates each field name of a bolded, comma-joined list through the
 * piece's namespace. The whole list is never a Tolgee key, and neither is a
 * name wrapped in asterisks — which is why field names stayed English while
 * step names (passed unwrapped) were translated.
 */
export const translateFieldNameList = (
  value: string,
  translate: (name: string) => string,
): string =>
  value.replaceAll(BOLD_FIELD_NAME, (match, name: string) => {
    const translated = translate(name.trim());
    return `**${translated}**`;
  });

// When a key is missing from the Tolgee catalog, i18next returns it verbatim
// without ICU formatting — fill the {param} placeholders ourselves so the user
// sees values instead of raw braces.
export const interpolateMissingIcuParams = (
  text: string,
  params: Record<string, string>,
): string =>
  text.replaceAll(/\{(\w+)\}/g, (match, paramName: string) =>
    paramName in params ? params[paramName] : match,
  );

/** Each step at every depth (loop bodies, router branches), with what it was configured WITH */
const stepInputs = (flowVersion: FlowVersion) =>
  flowStructureUtil.getAllSteps(flowVersion).map((step) => ({
    name: step.name,
    input: (step.settings as { input?: unknown }).input,
  }));

/**
 * Whether the turn changed the flow's SHAPE — steps added, removed, renamed,
 * moved, at any depth. Drives how the answer is applied: a shape change
 * rebuilds and re-lays out the canvas, while values alone take the diff-aware
 * merge. Values must NOT count here: on a configure turn the server version
 * is identical to (or staler than) what the user edits in the panel, and
 * replacing it resets the open form — which the panel's autosave then
 * persists. The canvas's own key answers it: the same key draws the same graph.
 */
export const hasFlowStructureChanged = (
  previous: FlowVersion,
  next: FlowVersion,
): boolean => canvasGraphKey(previous) !== canvasGraphKey(next);

/**
 * Whether the turn changed ANYTHING in the flow, values included. Drives the
 * undo affordance: filling a field is exactly what a user may want to revert.
 */
export const hasFlowChanged = (
  previous: FlowVersion,
  next: FlowVersion,
): boolean =>
  hasFlowStructureChanged(previous, next) ||
  !deepEqual(stepInputs(previous), stepInputs(next));

/**
 * The typed send for a "help me with this step" request: the server looks the
 * step up in the flow itself, so nothing but its name travels — never its
 * piece, its action or its test output.
 */
export const needHelpOn = (
  stepName: string,
): { input: AiBuilderTurnInput; about: AiBuilderAbout } => ({
  input: { type: 'need_help' },
  about: { type: 'step', stepName },
});

type CardChoiceOption = { label: string; value: unknown };

export type CardSelectChoices = {
  /** Which card a reply targets — built from the card's own typed fields, never a split id */
  target: CardTarget;
  /** What is being chosen — feeds the select placeholder */
  subject: string;
  /** The whole list, reached by search */
  options: CardChoiceOption[];
  /** Shown before any search: what matches the target the user named */
  featured: CardChoiceOption[];
};

/** The piece's Tolgee namespace, when it ships translations */
export const pieceNamespaceFor = (
  pieceName: string | undefined,
): string | undefined =>
  pieceName && AVAILABLE_PIECES_TRANSLATE.includes(pieceName)
    ? pieceName
    : undefined;

/**
 * The card's subject is a prop label from the piece metadata: translated
 * through the piece's namespace, like the sentence right above the card.
 */
export const localiseCardSubject = (
  choices: CardSelectChoices | null,
  pieceName: string | undefined,
  translate: (key: string, ns?: string) => string,
): CardSelectChoices | null => {
  if (isNil(choices)) return null;
  const ns = pieceNamespaceFor(pieceName);
  return ns ? { ...choices, subject: translate(choices.subject, ns) } : choices;
};

const toCardChoice = (option: CardDropdownOption): CardChoiceOption => ({
  label: String(option.label),
  value: option.value,
});

// The search box's subject for a loop's list, as the server's own list card names it
const LOOP_LISTS_SUBJECT = 'Items';

/**
 * The card types the chat renders as a real select: any unbounded list of
 * values. Short fixed choices (write preview, zero-connection gate) stay
 * quick-reply buttons — one click beats two.
 */
export const toCardSelectChoices = (
  card: AiBuilderCard | undefined,
  /** Tolgee lookup for the options the card names by a message key */
  translate: (key: string) => string,
): CardSelectChoices | null => {
  if (isNil(card)) return null;
  // Exhaustive on purpose (no default): a new `AiBuilderCardType` member
  // fails this function to compile until it is given a case here too.
  switch (card.type) {
    case 'dropdown_choice': {
      return {
        target: cardTargetOf(card),
        subject: card.propDisplayName,
        options: card.allOptions.map(toCardChoice),
        featured: card.shortlist.map(toCardChoice),
      };
    }
    case 'connection_gate': {
      if (card.connections.length <= 1) return null;
      const options = card.connections.map((connection) => ({
        label: connection.displayName,
        value: connection.externalId,
      }));
      return {
        target: cardTargetOf(card),
        subject: card.pieceDisplayName,
        options,
        featured: options,
      };
    }
    case 'write_preview':
      return null;
    case 'field_choice':
      return {
        target: cardTargetOf(card),
        subject: card.fieldDisplayName,
        options: card.allOptions.map(toCardChoice),
        featured: card.shortlist.map(toCardChoice),
      };
    case 'loop_lists_choice': {
      // Walking every list is always offered, never hidden behind a search
      const allLists = {
        label: translate(AiBuilderMessageKey.loopAllLists),
        value: card.allLists,
      };
      return {
        target: cardTargetOf(card),
        subject: LOOP_LISTS_SUBJECT,
        options: [...card.allOptions.map(toCardChoice), allLists],
        featured: [...card.shortlist.map(toCardChoice), allLists],
      };
    }
  }
};

// Combining marks left behind by NFD normalization
const DIACRITICS = /[\u0300-\u036F]/g;

// Punctuation separates words like a space: "gitlab qa" finds gitlab-qa-514-a,
// a run of separators reading as one space
const SEARCH_SEPARATORS = /[^\p{L}\p{N}]+/gu;

const normalizeForSearch = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(DIACRITICS, '')
    .replaceAll(SEARCH_SEPARATORS, ' ')
    .trim();

/**
 * What the card lists for a search: a Slack workspace returns up to 2000
 * channels, so the card never lists more than a handful — the shortlist
 * before any search, the first matches of the WHOLE list after.
 */
export const visibleCardChoices = (
  choices: CardSelectChoices,
  query: string,
): { visible: CardChoiceOption[]; hiddenCount: number } => {
  const needle = normalizeForSearch(query);
  if (needle === '') {
    const visible = (
      choices.featured.length > 0 ? choices.featured : choices.options
    ).slice(0, AI_BUILDER_CARD_VISIBLE_OPTIONS);
    return { visible, hiddenCount: choices.options.length - visible.length };
  }
  const matches = choices.options.filter((option) =>
    normalizeForSearch(option.label).includes(needle),
  );
  const visible = matches.slice(0, AI_BUILDER_CARD_VISIBLE_OPTIONS);
  return { visible, hiddenCount: matches.length - visible.length };
};

/**
 * A key is translated; literal text is shown as the model or the user wrote
 * it — the one function that reads `AiBuilderText.kind` instead of guessing
 * from whether params happen to be present.
 */
export const localiseAiBuilderText = (
  text: AiBuilderText,
  translate: (key: string, params?: Record<string, string>) => string,
): string =>
  text.kind === 'key' ? translate(text.key, text.params) : text.text;

/**
 * What a suggested action actually SENDS, resolved at click time. A
 * protocol-shaped send (step_done, card_reply, …) is final. A `text` send is
 * a KEY to translate only when the button's label is itself a key: the server
 * cannot know the user's locale. When the label is literal text, `send.text`
 * is already the final words — running model or user text (a connection
 * name) through `t()` would be wrong.
 */
export const sendInputFor = (
  action: SuggestedAction,
  translate: (key: string, params?: Record<string, string>) => string,
): AiBuilderTurnInput =>
  action.send.type === 'text' && action.label.kind === 'key'
    ? { type: 'text', text: translate(action.send.text) }
    : action.send;

type ConversationMessage = {
  userType: 'user' | 'bot';
  body: AiBuilderText;
  answeredChoice?: string;
  narration?: boolean;
};

/** A turn's own input, kept until its answer is stored — see `isAnswerLost` */
export type PendingTurnInput = {
  input: AiBuilderTurnInput;
  sentAt: string;
};

/**
 * The page sent something — a typed line, a button click, help, a resume or a
 * card reply — and never got an answer back. The one source of truth is "did
 * we send and not yet clear it", never a guess read off the chat's content.
 * Cleared when the turn settles, success or failure alike; persisted, so a
 * page reloaded mid-turn still reads it as lost.
 */
export const isAnswerLost = (
  pendingInput: PendingTurnInput | undefined,
  isTurnInFlight: boolean,
): boolean => pendingInput !== undefined && !isTurnInFlight;

/**
 * What still waits for an answer once a turn settles: nothing, unless the
 * server refused it as busy. That refusal answers nothing, so what was
 * pending before this send (a turn lost to a reload, still running
 * server-side) still is.
 */
export const pendingInputAfterTurn = (
  response: AskAiBuilderResponse | undefined,
  pendingBeforeSend: PendingTurnInput | undefined,
): PendingTurnInput | undefined =>
  response?.message?.body &&
  isKeyText(response.message.body, AiBuilderSystemMessage.flowBusy)
    ? pendingBeforeSend
    : undefined;

/**
 * A mid-turn event (progress, partial flow update, narration) naming a
 * DIFFERENT, already-known turn: a previous turn still finishing after a
 * retry or a second tab started a new one on the same flow. Absent on either
 * side — an event with no requestId, or no turn tracked yet for this page —
 * means "can't tell": never dropped on that alone, so an old client (or the
 * silent BUILD path) is unaffected.
 */
export const isStaleTurnEvent = (
  eventRequestId: string | undefined,
  currentRequestId: string | undefined,
): boolean =>
  eventRequestId !== undefined &&
  currentRequestId !== undefined &&
  eventRequestId !== currentRequestId;

/**
 * How many messages travel with a turn. The socket schema rejects a context
 * longer than AI_BUILDER_CONTEXT_HARD_CAP — every turn of a long session would
 * then fail validation with nothing to show for it — and the server replays
 * only its own last 20 anyway, so a bigger window buys nothing.
 */
const MAX_SENT_MESSAGES = 40;

/**
 * At most `max` messages: the first USER message, then the tail. The server
 * reads the original intent from the first user message it receives, so
 * dropping it — from what travels or from what a reload restores — would
 * silently rewrite what the whole build was asked to do.
 */
export const windowKeepingIntent = <T extends { userType: 'user' | 'bot' }>(
  messages: T[],
  max: number,
): T[] => {
  if (messages.length <= max) return messages;
  const tail = messages.slice(-max);
  const firstUserMessage = messages.find(
    (message) => message.userType === 'user',
  );
  return firstUserMessage && !tail.includes(firstUserMessage)
    ? [firstUserMessage, ...tail.slice(1)]
    : tail;
};

type PendingReplyMessage = Pick<
  AiBuilderChatMessage,
  'userType' | 'pendingQuestion' | 'replyChipDismissed'
>;

/**
 * What the last bot answer is waiting on, read once: the chip (`replyChipOf`)
 * and the wire's `replyTo` (`replyToOf`) are two views of the SAME fact —
 * reading it twice, independently, is how the two would drift.
 */
const lastPendingQuestion = (
  messages: PendingReplyMessage[],
): { pendingQuestion: PendingQuestionDisplay; dismissed: boolean } | null => {
  const last = messages[messages.length - 1];
  if (last?.userType !== 'bot' || !last.pendingQuestion) return null;
  return {
    pendingQuestion: last.pendingQuestion,
    dismissed: last.replyChipDismissed === true,
  };
};

/**
 * The "Réponse à" chip: shown only while the conversation ends exactly where
 * the server left it waiting — the last message is the bot answer that asked,
 * untouched since, and not dismissed. One source: derived fresh from the
 * stored messages on every read, never a second, separately-updated field.
 */
export const replyChipOf = (
  messages: PendingReplyMessage[],
): PendingQuestionDisplay | null => {
  const found = lastPendingQuestion(messages);
  return found && !found.dismissed ? found.pendingQuestion : null;
};

/**
 * GenerateFlowRequest.replyTo: absent when nothing is pending, the question's
 * id when the chip is still shown (a strong hint this message answers it),
 * `'none'` when the user dismissed it with the cross.
 */
export const replyToOf = (
  messages: PendingReplyMessage[],
): GenerateFlowRequest['replyTo'] => {
  const found = lastPendingQuestion(messages);
  if (!found) return undefined;
  return found.dismissed ? 'none' : { questionId: found.pendingQuestion.id };
};

/**
 * The bot message for one turn's answer — the one shape both the chat
 * history (addMessage) and the page-level applier (recordAnswerToApply) read
 * from a response. `previousFlowVersion`/`sourceInput` come from the hook's
 * own refs, captured when the turn was sent: the response itself never
 * carries them.
 */
export const turnAnswerMessages = (
  response: AskAiBuilderResponse,
  sent: {
    previousFlowVersion: FlowVersion | null;
    sourceInput: AiBuilderTurnInput | undefined;
  },
): AiBuilderChatMessage => ({
  // A server of the previous release (rolling deploy) answers without `body`
  body: response.message?.body ?? keyText(AiBuilderSystemMessage.turnFailed),
  prefix: response.message?.prefix,
  changes: response.message?.changes,
  userType: 'bot',
  previousFlowVersion: sent.previousFlowVersion,
  newFlowVersion: response.flow?.version,
  suggestedActions: response.suggestedActions,
  card: response.card,
  sourceInput: sent.sourceInput,
  flowReady: response.flowReady,
  navigateDirectives: response.navigateDirectives,
  pendingQuestion: response.pendingQuestion,
});

/**
 * The conversation as the model must read it. A choice picked on a card or a
 * button is stored ON the bot message it answers (so the UI can show it in
 * place), but for the model it is a user turn like any other — it is what the
 * user said.
 */
export const toChatContext = (
  messages: ConversationMessage[],
): { role: 'user' | 'assistant'; content: string }[] =>
  windowKeepingIntent(messages, MAX_SENT_MESSAGES).flatMap((message) => {
    const own = {
      role:
        message.userType === 'user'
          ? ('user' as const)
          : ('assistant' as const),
      // The model reads what was said, not a key: a phrased question rides
      // as a param, and a bare key would leave "{question}" in its place
      content:
        message.body.kind === 'key'
          ? interpolateMissingIcuParams(message.body.key, message.body.params)
          : message.body.text,
    };
    return message.answeredChoice
      ? [own, { role: 'user' as const, content: message.answeredChoice }]
      : [own];
  });
